import { inject, Injectable } from '@angular/core';
import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import {
  Logins,
  fromCognitoIdentityPool,
} from '@aws-sdk/credential-provider-cognito-identity';
import { environment } from 'src/environments/environment';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Observable } from 'rxjs';
import { NetworkService } from './network.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private client?: S3Client;
  private clientExpirationDate?: Date;

  private networkService = inject(NetworkService);

  public async mockUploadFile(file: File, filePath: string): Promise<void> {
    const shouldFail = Math.random() < 0.1;
    const uploadTime = 300 + Math.random() * 500;

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (shouldFail) {
          reject(new Error(`Mock upload failed for ${filePath}`));
        } else {
          resolve();
        }
      }, uploadTime);
    });
  }

  public async uploadFile(file: File, filePath: string) {
    const abortController = new AbortController();
     this.networkService.onStatusChange((isOffline) => {
      if (isOffline) {
        abortController.abort();
      } 
    });

    const [client, buffer] = await Promise.all([
      this.getS3Client(),
      file.arrayBuffer(),
    ]);
    const command = new PutObjectCommand({
      Bucket: environment.bucketName,
      Key: filePath,
      Body: new Uint8Array(buffer),
      ContentType: file.type,
    });

    await client.send(command, { abortSignal: abortController.signal });
  }

  private async getS3Client() {
    if (
      this.client &&
      this.clientExpirationDate &&
      Date.now() < this.clientExpirationDate.getTime() - 10 * 60 * 1000
    ) {
      return this.client;
    }

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString()!;

    const { region, userPool, identityPoolId } = environment.authConfig;

    const logins: Logins = {
      [`cognito-idp.${region}.amazonaws.com/${userPool}`]: idToken,
    };

    const credentialsProvider = fromCognitoIdentityPool({
      client: new CognitoIdentityClient({ region }),
      identityPoolId: `${region}:${identityPoolId}`,
      logins,
    });

    const credentials = await credentialsProvider();

    const client = new S3Client({
      region,
      credentials,
      requestChecksumCalculation: 'WHEN_REQUIRED',
      useAccelerateEndpoint: true
    });

    this.clientExpirationDate = credentials.expiration;

    this.client = client;
    return client;
  }
}
